import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowRightOutlined,
  CustomerServiceOutlined,
  HomeOutlined,
  PlusOutlined,
  SendOutlined,
} from "@ant-design/icons";

import { PortalApi } from "../../../services";
import { EmptyState, ModalTitle } from "../../../components";
import {
  TICKET_PRIORITIES,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUSES,
} from "../../Customers/constants";
import styles from "../styles.module.css";

const { Title, Text } = Typography;

const Crumbs = ({ title }) => (
  <Breadcrumb
    className="mb-3"
    items={[
      {
        title: (
          <>
            <HomeOutlined />
            <span>پرتال</span>
          </>
        ),
      },
      { title },
    ]}
  />
);

const PageHeader = ({ action }) => (
  <>
    <Crumbs title="درخواست‌های من" />
    <header className={styles.header}>
    <span>
      <CustomerServiceOutlined />
    </span>
    <div style={{ flex: 1 }}>
      <Title level={4}>درخواست‌های من</Title>
      <Text>هر سؤال یا درخواستی دارید همین‌جا ثبت و پیگیری می‌شود</Text>
    </div>
    {action}
    </header>
  </>
);

/* درخواست‌های خودِ مشتری. تنها جایی در پرتال که مشتری چیزی می‌نویسد، پس عمداً
 * ساده است: موضوع، شرح، اولویت — و بعد یک گفتگوی خطی. */

const jalali = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const PortalTickets = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [composerOpen, setComposerOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [form] = Form.useForm();

  /* تیکت باز در URL می‌نشیند: بازگشت مرورگر کار می‌کند، رفرش آدم را به لیست
     برنمی‌گرداند و لینک یک درخواست قابل اشتراک است. */
  const selected = searchParams.get("ticket");
  const openTicket = (uuid) => setSearchParams({ ticket: uuid });
  const backToList = () => setSearchParams({});

  const list = useQuery({
    queryKey: ["[Portal] Tickets"],
    queryFn: () => PortalApi.getPortalTickets().then((res) => res.data.tickets || []),
    initialData: [],
  });

  const detail = useQuery({
    queryKey: ["[Portal] Ticket", selected],
    queryFn: () =>
      PortalApi.getPortalTicket({ params: { uuid: selected } }).then(
        (res) => res.data.ticket,
      ),
    enabled: Boolean(selected),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["[Portal] Tickets"] });
    if (selected) {
      queryClient.invalidateQueries({ queryKey: ["[Portal] Ticket", selected] });
    }
  };

  const fail = (fallback) => (error) =>
    message.error({ content: error?.response?.data?.message || fallback });

  const create = useMutation(PortalApi.createPortalTicket, {
    onSuccess: (res) => {
      message.success({ content: "درخواست شما ثبت شد" });
      setComposerOpen(false);
      refresh();
      if (res.data.ticket?.uuid) openTicket(res.data.ticket.uuid);
    },
    onError: fail("ثبت درخواست انجام نشد"),
  });

  const reply = useMutation(PortalApi.replyPortalTicket, {
    onSuccess: () => {
      setReplyText("");
      refresh();
    },
    onError: fail("ثبت پاسخ انجام نشد"),
  });

  const ticket = detail.data;

  if (selected && detail.isFetching && !ticket) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (selected && ticket) {
    return (
      <div className="w-100">
        <Button
          type="text"
          icon={<ArrowRightOutlined />}
          className="mb-3"
          onClick={backToList}
        >
          بازگشت به درخواست‌ها
        </Button>

        <div className="ds-panel p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
            <strong>{ticket.subject}</strong>
            <Tag color={TICKET_STATUSES[ticket.status]?.color}>
              {TICKET_STATUSES[ticket.status]?.label}
            </Tag>
          </div>
          <span className={styles.meta}>
            {jalali(ticket.created_at)}
            {ticket.assignee && ` · پیگیری: ${ticket.assignee.full_name}`}
          </span>
        </div>

        <div className={styles.thread}>
          <div className={`${styles.reply} ${styles.mine}`}>
            <div className={styles.replyHead}>
              <strong>{ticket.contact?.name || "شما"}</strong>
              <span>{jalali(ticket.created_at)}</span>
            </div>
            <div className={styles.replyBody}>{ticket.body || "—"}</div>
          </div>

          {(ticket.replies || []).map((row) => (
            <div
              className={`${styles.reply} ${row.customer_contact_id ? styles.mine : ""}`}
              key={row.id}
            >
              <div className={styles.replyHead}>
                <strong>
                  {row.contact?.name || row.user?.full_name || "پشتیبانی"}
                </strong>
                <span>{jalali(row.created_at)}</span>
              </div>
              <div className={styles.replyBody}>{row.body}</div>
            </div>
          ))}
        </div>

        {ticket.status === "closed" ? (
          <div className={`${styles.meta} mt-3`}>
            این درخواست بسته شده است. برای پیگیری تازه، درخواست جدید ثبت کنید.
          </div>
        ) : (
          <div className="mt-3 d-flex flex-column gap-2">
            <Input.TextArea
              rows={3}
              value={replyText}
              placeholder="پاسخ شما"
              onChange={(event) => setReplyText(event.target.value)}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={reply.isLoading}
              style={{ alignSelf: "flex-start" }}
              onClick={() => {
                const body = replyText.trim();
                if (!body) return message.warning({ content: "متن پاسخ خالی است" });
                return reply.mutate({ params: { uuid: selected }, body: { body } });
              }}
            >
              ثبت پاسخ
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-100">
      <PageHeader
        action={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setComposerOpen(true);
            }}
          >
            درخواست جدید
          </Button>
        }
      />

      {list.isFetching && list.data.length === 0 ? (
        <div className={styles.grid}>
          {[1, 2].map((key) => (
            <div className="ds-panel p-3" key={key}>
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
      ) : list.data.length === 0 ? (
        <EmptyState
          icon={<CustomerServiceOutlined />}
          title="درخواستی ثبت نکرده‌اید"
          description="هر سؤال یا درخواستی که دارید همین‌جا ثبت کنید تا پیگیری شود."
          actionLabel="درخواست جدید"
          actionIcon={<PlusOutlined />}
          onAction={() => setComposerOpen(true)}
        />
      ) : (
        <div className={styles.grid}>
          {list.data.map((row) => (
            <button
              type="button"
              className={styles.card}
              key={row.uuid}
              onClick={() => openTicket(row.uuid)}
            >
              <div className="d-flex align-items-center justify-content-between gap-2 w-100">
                <strong>{row.subject}</strong>
                <Tag color={TICKET_STATUSES[row.status]?.color}>
                  {TICKET_STATUSES[row.status]?.label}
                </Tag>
              </div>
              <span className={styles.meta}>
                {jalali(row.created_at)} · اولویت {TICKET_PRIORITIES[row.priority]?.label}
              </span>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={composerOpen}
        title={
          <ModalTitle
            icon={<CustomerServiceOutlined />}
            tone="purple"
            title="درخواست جدید"
            subtitle="خواسته‌تان را بنویسید تا پیگیری شود"
          />
        }
        className="ds-modal"
        centered
        width={520}
        okText="ثبت درخواست"
        cancelText="انصراف"
        confirmLoading={create.isLoading}
        onOk={() => form.submit()}
        onCancel={() => setComposerOpen(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ priority: "normal" }}
          onFinish={(values) => create.mutate({ body: values })}
        >
          <Form.Item
            name="subject"
            label="موضوع"
            rules={[{ required: true, message: "موضوع را بنویسید" }]}
          >
            <Input placeholder="خواسته‌تان در یک جمله" maxLength={255} />
          </Form.Item>
          <Form.Item name="body" label="شرح">
            <Input.TextArea rows={4} placeholder="هر جزئیاتی که کمک می‌کند" />
          </Form.Item>
          <Form.Item name="priority" label="اولویت">
            <Select options={TICKET_PRIORITY_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PortalTickets;
